import { CalendarDaysIcon, HeartIcon as HeartIconOutlined } from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import { memberClient } from "common/apis/member";
import LoginContainer from "common/containers/Account/Login";
import { MemberAuthProvider, useMemberAuth } from "common/contexts/Auth/Member";
import Button from "components/Form/Button";
import { useModal } from "components/Modal/Simple";
import SpinnerMedium from "components/Spinner/Medium";
import PageTitle from "components/Title/Page";
import _, { set } from "lodash";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import lineLogo from "common/containers/Account/Login/logos/line.svg";
import Card from "components/Card";
import Textarea from "components/Form/Textarea";

type BookmarkableTypes = "CLINIC" | "DOCTOR" | "EVENT" | "REVIEW";

const CounselForm = ({ clinicId, bookmarkableType, bookmarkableId, onSubmit }) => {
    const [content, setContent] = useState("");

    const handleBasicSubmitClick = async () => {
        try {
            const body = {
                clinic_id: clinicId,
                sourceType: bookmarkableType,
                sourceId: bookmarkableId,
                content
            }
            const ret = await memberClient.post(`/member/counsels`, body);
            console.log("[handleSubmitClick] ret", ret);
            toast.success("상담이 예약되었습니다.");
            onSubmit();
        } catch (e) {
            console.error("[handleLineClick] error", e);
        }
    }

    const handleLineSubmitClick = async () => {
        try {
            const body = {
                clinic_id: clinicId,
                sourceType: bookmarkableType,
                sourceId: bookmarkableId,
                content
            }
            const ret = await memberClient.post(`/member/counsels`, body);
            console.log("[handleSubmitClick] ret", ret);
            toast.success("상담이 예약되었습니다.");
            window.open("https://lin.ee/PRwLcKq", "newWindow");
            onSubmit();
        } catch (e) {
            console.error("[handleLineClick] error", e);
        }
    }

    return (
        <div>
            <PageTitle>
                상담 예약하기
            </PageTitle>
            <div className="mb-2">
                <Textarea
                    placeholder="라인 상담을 원하시면 라인 ID를 남겨주세요. 담당자가 확인 후 연락드리겠습니다."
                    minRows={5}
                    value={content}
                    onChange={setContent}
                />
            </div>
            <div className="flex justify-center mt-3" onClick={handleBasicSubmitClick}>
                <Card>
                    <div className="p-3 flex justify-center space-x-2 items-center cursor-pointer min-w-[250px]">
                        <CalendarDaysIcon className="size-5" />
                        <div className="self-center">
                            {"상담 예약"}
                        </div>
                    </div>
                </Card>
            </div>
            <div className="flex justify-center text-md text-gray-500">
                {"또는"}
            </div>
            <div className="flex justify-center" onClick={handleLineSubmitClick}>
                <Card>
                    <div className="p-3 flex justify-center cursor-pointer min-w-[250px]">
                        <img src={lineLogo} alt="line-logo" className="w-7 h-7 mr-2" />
                        <div className="self-center">
                            {"라인 친구추가"}
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    )
}

const BottomBookmarkBarForm = ({ clinicId, bookmarkableId, bookmarkableType }: { clinicId: number, bookmarkableId: number, bookmarkableType: BookmarkableTypes }) => {
    const [isBookmarked, setIsBookmarked] = useState(null);
    const [bookmarkId, setBookmarkId] = useState(null);
    const { Modal: LoginModal, openModal: openLoginModal, closeModal: closeLoginModal } = useModal();
    const { Modal: CounselModal, openModal: openCounselModal, closeModal: closeCounselModal } = useModal();
    const { member, reload } = useMemberAuth();

    useEffect(() => {
        fetchBookmark();
    }, [member])

    const fetchBookmark = async () => {
        if (_.isNil(member)) {
            setIsBookmarked(false);
            return;
        }
        try {
            const ret = await memberClient.get(`/member/bookmarks/${bookmarkableType.toLowerCase()}/${bookmarkableId}`);
            setIsBookmarked(!_.isEmpty(ret?.data));
            setBookmarkId(ret?.data?.[0]?.id);
            console.log("[fetchBookmark] ret", ret);
        } catch (e) {
            console.error("[fetchBookmark] error", e);
        }
    }

    const handleBookmarkClick = async () => {
        if (_.isNil(member)) { // bookmark loading is failed so not logined yet
            openLoginModal();
            return;
        }
        try {
            if (isBookmarked) {
                const ret = await memberClient.delete(`/member/bookmarks/${bookmarkId}`);
                console.log("[handleBookmarkClick] remove ret", ret);
                toast.success("찜이 해제되었습니다.");
            } else {
                // const ret = await memberClient.post(`/bookmark/${bookmarkableType.toLowerCase()}/${bookmarkableId}`);
                const body = {
                    bookmarkableId,
                    bookmarkableType
                }
                const ret = await memberClient.post(`/member/bookmarks`, body);
                console.log("[handleBookmarkClick] post ret", ret);
                toast.success("찜이 추가되었습니다.");
            }
            fetchBookmark();
        } catch (e) {
            console.error("[handleBookmarkClick] error", e);
        }
    }

    const handleCounselClick = async () => {
        console.log("[handleCounselClick]", bookmarkableType);
        if (_.isNil(member)) { // bookmark loading is failed so not logined yet
            openLoginModal();
            return;
        }
        openCounselModal();
    }

    const handleLoggedIn = async () => {
        closeLoginModal();
        await reload();
    }

    return (
        // max-w-sm temporary, someday extract it to a custom css classname for global use
        <div className="fixed bottom-[63px] bg-white w-full max-w-md">
            <div className="border-t border-gray-200"></div>
            <div className="flex py-3 px-5 space-x-2">
                <div>
                    <Button onButtonClicked={handleBookmarkClick}>
                        <div className="flex items-center">
                            {isBookmarked ? (
                                <>
                                    <HeartIconSolid className="size-5 text-white" />
                                    <div className="ps-1">
                                        찜 해제
                                    </div>

                                </>
                            ) : (
                                <>
                                    <HeartIconOutlined className="size-5 text-white" />
                                    <div className="ps-1">
                                        찜하기
                                    </div>
                                </>
                            )}
                        </div>
                    </Button>
                </div>
                <Button onButtonClicked={handleCounselClick}>
                    <CalendarDaysIcon className="size-5 text-white" />
                    <div className="ps-1">
                        {"상담예약"}
                    </div>
                </Button>
            </div >
            <div className="border-b border-gray-200"></div>
            <LoginModal>
                <div>
                    <div className="text-md ps-4">
                        로그인이 필요한 서비스 입니다
                    </div>
                    <LoginContainer onLoggedIn={handleLoggedIn} />
                </div>
            </LoginModal>
            <CounselModal>
                <CounselForm
                    clinicId={clinicId}
                    bookmarkableType={bookmarkableType}
                    bookmarkableId={bookmarkableId}
                    onSubmit={closeCounselModal}
                />
            </CounselModal >
        </div >
    )
}

const BottomBookmarkBar = ({ clinicId, bookmarkableId, bookmarkableType }: { clinicId: number, bookmarkableId: number, bookmarkableType: BookmarkableTypes }) => {
    return (
        <MemberAuthProvider>
            <BottomBookmarkBarForm
                clinicId={clinicId}
                bookmarkableId={bookmarkableId}
                bookmarkableType={bookmarkableType}
            />
        </MemberAuthProvider>
    )
}

export default BottomBookmarkBar;